import { ReleaseGraph } from "./../../../Models/ServerModels/Calculations/ReleaseGraph";
import { FarmerPractice, FarmerPracticeViewModel, GenericFormula } from "./../../../Models/ServerModels/SavedItems/FarmerPractice";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CalculateCustomizedSolutionProductResponse, CalculateRecommendedSolutionProductResponse, Recipe, BlendStat, UnitValuePair } from "../../../Models";
import { RawMaterial } from "../../../Models/ServerModels/RawMaterials/GetAllRawMaterialsResponse";
import { SavedItemsYield } from "../../../Models/ServerModels/SavedItems/SavedItemsYield";
import { shareableItemTypes } from "../../../Utils/Enums";
import crops from "../../../Translations/Languages/he/crops";

interface AgriSolutionSliceState {
    cropsState: {
        selectedCrop: string | undefined;
        expectedYield: number | undefined;
        plantingDate: string | undefined;
        finalHarvestDate: string | undefined;
        selectedLocationCountryCode: string | undefined;
        selectedLocationLat: number;
        selectedLocationLng: number;
        metricUnit: string | undefined;
    };
    yield: SavedItemsYield[];
    farmState: {
        cropDemand: FarmerPracticeViewModel[];
        farmerPractice: FarmerPractice;
    };
    carbonBannerShown: boolean;
    temperatureState: {
        temperatures: { date: number; value: number; index: number }[];
        selectedSoil: number;
        selectedWater: number;
        selectedTemperatureUnit: string | undefined;
    };
    resultsState: {
        recommendedSolution: CalculateRecommendedSolutionProductResponse | null;
        customizedSolution: CalculateCustomizedSolutionProductResponse | null;
    };
    recommendedSolutionName: string | null;
    recommendedSolutionMaterialCode: string | null;
    customizedState: {
        allCoatedMaterials: RawMaterial[] | null;
        allUnCoatedMaterials: RawMaterial[] | null;
        unCoatedMaterialsMadeByUser: string[] | null;
        blendPercentages: number | null;
        recipe: Recipe;
        bestSolutionFormula: string | null;
        updatedBestSolutionFormula: string | null;
        updatedCoatingPercentage: FarmerPractice;
        customizedFormulaByUser: string;
        bestSuitFormulaCoatings: string | null;
        formulaNutrients: FarmerPractice;
        displayUpdateButton: boolean;
    };
    releaseGraphState: {
        farmerPracticeGraphString: string;
        isUserShare: boolean;
        shareLink: string;
        customizedReleaseGraph: ReleaseGraph[] | null;
        shelfSolutionReleaseGraph: ReleaseGraph[] | null;
        displayReleaseGraphType: shareableItemTypes;
        applicationVolume: UnitValuePair[];
        shelfSolutionCurrentDayReleaseRemainValues: GenericFormula[];
        customizedSolutionCurrentDayReleaseRemainValues: GenericFormula[];
        isGreenDescriptionOpen: boolean;
        isCarbonTableOpen: boolean;
        isIndexDialogOpen: boolean;
    };
    isUserSave: boolean;
    itemId: number | null;
    itemName: string;
    saveInProgress: boolean;
    showMobileMap: boolean;
    stepIndexPage: number;
    isAgriItem: boolean;
    soilStateCanBeNavigated: boolean;
    resultsStateCanBeNavigated: boolean;
    farmStateCanBeNavigated: boolean;
    customizeStateCanBeNavigated: boolean;
    rawMaterialBank: RawMaterial[];
}

const initialState: AgriSolutionSliceState = {
    carbonBannerShown: true,
    cropsState: {
        selectedCrop: undefined,
        expectedYield: undefined,
        plantingDate: undefined,
        finalHarvestDate: undefined,
        selectedLocationCountryCode: undefined,
        selectedLocationLat: 29.861419802885575,
        selectedLocationLng: 31.590398389979242,
        metricUnit: undefined,
    },
    temperatureState: {
        temperatures: [],
        selectedSoil: 1,
        selectedWater: 1,
        selectedTemperatureUnit: undefined,
    },
    customizedState: {
        displayUpdateButton: false,
        allCoatedMaterials: null,
        allUnCoatedMaterials: null,
        unCoatedMaterialsMadeByUser: null,
        blendPercentages: null,
        recipe: {
            coated: [],
            unCoated: [],
        },
        bestSolutionFormula: "",
        updatedBestSolutionFormula: "",
        customizedFormulaByUser: "",
        bestSuitFormulaCoatings: "",
        formulaNutrients: {
            n: null,
            p: null,
            k: null,
        },
        updatedCoatingPercentage: {
            n: null,
            p: null,
            k: null,
        },
    },
    yield: [],
    farmState: {
        cropDemand: [],
        farmerPractice: {
            n: null,
            p: null,
            k: null,
        },
    },
    releaseGraphState: {
        farmerPracticeGraphString: "",
        isIndexDialogOpen: false,
        isUserShare: false,
        shareLink: "",
        customizedReleaseGraph: null,
        shelfSolutionReleaseGraph: null,
        displayReleaseGraphType: shareableItemTypes.empty,
        applicationVolume: [],
        shelfSolutionCurrentDayReleaseRemainValues: [],
        customizedSolutionCurrentDayReleaseRemainValues: [],
        isGreenDescriptionOpen: true,
        isCarbonTableOpen: false,
    },
    isUserSave: false,
    saveInProgress: false,
    showMobileMap: false,
    itemId: null,
    itemName: "",
    soilStateCanBeNavigated: false,
    resultsStateCanBeNavigated: false,
    farmStateCanBeNavigated: false,
    customizeStateCanBeNavigated: false,
    resultsState: {
        recommendedSolution: null,
        customizedSolution: null,
    },
    recommendedSolutionName: "",
    recommendedSolutionMaterialCode: "",
    stepIndexPage: 0,
    isAgriItem: true,
    rawMaterialBank: [],
};

export const agriSolution = createSlice({
    name: "agriSolution",
    initialState,
    reducers: {
        toggleIndexDialog: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            return {
                ...state,
                releaseGraphState: {
                    ...state.releaseGraphState,
                    isIndexDialogOpen: action.payload,
                },
            };
        },
        hideCarbonBanner: (state: AgriSolutionSliceState) => {
            state.carbonBannerShown = false;
        },
        toggleCarbonTable: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            return {
                ...state,
                releaseGraphState: {
                    ...state.releaseGraphState,
                    isCarbonTableOpen: action.payload,
                },
            };
        },
        setSelectedCropsState: (
            state: AgriSolutionSliceState,
            action: PayloadAction<{
                selectedCrop: string | undefined;
                expectedYield: number | undefined;
                plantingDate: string | undefined;
                finalHarvestDate: string | undefined;
                selectedLocationCountryCode: string | undefined;
                selectedLocationLat: number;
                selectedLocationLng: number;
                metricUnit: string | undefined;
            }>
        ) => {
            state.cropsState = action.payload;
        },

        setSelectedCrop: (state: AgriSolutionSliceState, action: PayloadAction<string | undefined>) => {
            state.cropsState.selectedCrop = action.payload;
        },

        setRawMaterialBank: (state: AgriSolutionSliceState, action: PayloadAction<RawMaterial[]>) => {
            state.rawMaterialBank = action.payload;
        },

        setShelfSolutionCurrentDayReleaseRemainValues: (state: AgriSolutionSliceState, action: PayloadAction<GenericFormula[]>) => {
            state.releaseGraphState.shelfSolutionCurrentDayReleaseRemainValues = action.payload;
        },
        setCustomizedSolutionCurrentDayReleaseRemainValues: (state: AgriSolutionSliceState, action: PayloadAction<GenericFormula[]>) => {
            state.releaseGraphState.customizedSolutionCurrentDayReleaseRemainValues = action.payload;
        },
        setYield: (state: AgriSolutionSliceState, action: PayloadAction<SavedItemsYield[]>) => {
            state.yield = action.payload;
        },
        setDisplayUpdateButton: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            state.customizedState.displayUpdateButton = action.payload;
        },
        setRecommendedSolution: (state: AgriSolutionSliceState, action: PayloadAction<string | null>) => {
            state.recommendedSolutionName = action.payload;
        },
        setRecommendedSolutionMaterialCode: (state: AgriSolutionSliceState, action: PayloadAction<string | null>) => {
            state.recommendedSolutionMaterialCode = action.payload;
        },
        setExpectedYield: (state: AgriSolutionSliceState, action: PayloadAction<number | undefined>) => {
            state.cropsState.expectedYield = action.payload;
        },
        setApplicationVolume: (state: AgriSolutionSliceState, action: PayloadAction<UnitValuePair[]>) => {
            state.releaseGraphState.applicationVolume = action.payload;
        },
        setSaveInProgress: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            state.saveInProgress = action.payload;
        },
        setReleaseGraph: (state: AgriSolutionSliceState, action: PayloadAction<ReleaseGraph[]>) => {
            console.log("setReleaseGraph", action.payload);

            state.releaseGraphState.customizedReleaseGraph = action.payload;
        },
        setShelfSolutionReleaseGraph: (state: AgriSolutionSliceState, action: PayloadAction<ReleaseGraph[] | null>) => {
            console.log("setShelfSolutionReleaseGraph", action.payload);

            state.releaseGraphState.customizedReleaseGraph = action.payload;
            state.releaseGraphState.shelfSolutionReleaseGraph = action.payload;
        },
        setReleaseGraphType: (state: AgriSolutionSliceState, action: PayloadAction<shareableItemTypes>) => {
            state.releaseGraphState.displayReleaseGraphType = action.payload;
        },
        setShareLink: (state: AgriSolutionSliceState, action: PayloadAction<string>) => {
            state.releaseGraphState.shareLink = action.payload;
        },
        setIsUserShare: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            state.releaseGraphState.isUserShare = action.payload;
        },
        setCropDemand: (state: AgriSolutionSliceState, action: PayloadAction<FarmerPracticeViewModel[]>) => {
            state.farmState.cropDemand = action.payload;
        },
        setFarmerPractice: (state: AgriSolutionSliceState, action: PayloadAction<FarmerPractice>) => {
            state.farmState.farmerPractice = action.payload;

            const arr: any[] = [];

            const remainingValues: FarmerPractice = state.farmState.farmerPractice ? state.farmState.farmerPractice : { n: 0, p: 0, k: 0 };

            if (remainingValues) {
                ["n", "p", "k"].map((i) => {
                    arr.push(((remainingValues as any)[i] * 100) / (state.farmState.farmerPractice as any)[i]);
                });
            }

            state.releaseGraphState.farmerPracticeGraphString = `${arr[0].toFixed(1)}, ${arr[1].toFixed(1)}, ${arr[2].toFixed(1)}%`;
        },
        setUpdatedCoatingPercentage: (state: AgriSolutionSliceState, action: PayloadAction<FarmerPractice>) => {
            state.customizedState.updatedCoatingPercentage = action.payload;
        },
        setRecipe: (state: AgriSolutionSliceState, action: PayloadAction<Recipe>) => {
            state.customizedState.recipe = action.payload;
        },
        addItemToRecipe: (state: AgriSolutionSliceState, action: PayloadAction<{ newItem: BlendStat; isCoated: boolean }>) => {
            if (action.payload.isCoated) {
                state.customizedState.recipe.coated.push(action.payload.newItem);

                const index = state.customizedState.allCoatedMaterials?.findIndex((i: any) => i.id == action.payload.newItem.id);
                if (typeof index === "number") state.customizedState.allCoatedMaterials?.splice(index, 1);
            } else {
                state.customizedState.recipe.unCoated.push(action.payload.newItem);
                const index = state.customizedState.allUnCoatedMaterials?.findIndex((i: any) => i.id == action.payload.newItem.id);
                if (index) state.customizedState.allUnCoatedMaterials?.splice(index, 1);
            }
            let sum = 0;

            for (let i = 0; i < state.customizedState.recipe.coated.length; i++) sum = sum + state.customizedState.recipe.coated[i].precentage;
            for (let i = 0; i < state.customizedState.recipe.unCoated.length; i++) sum = sum + state.customizedState.recipe.unCoated[i].precentage;

            state.customizedState.blendPercentages = sum;
        },
        updatePercentForItemInRecipe: (
            state: AgriSolutionSliceState,
            action: PayloadAction<{
                item: BlendStat;
                isCoated: boolean;
                value: number;
            }>
        ) => {
            let sum = 0;
            if (action.payload.isCoated) {
                const index = state.customizedState.recipe.coated.findIndex((i) => i.id == action.payload.item.id);
                state.customizedState.recipe.coated[index].precentage = Number(action.payload.value);
            } else {
                const index = state.customizedState.recipe.unCoated.findIndex((i) => i.id == action.payload.item.id);
                state.customizedState.recipe.unCoated[index].precentage = Number(action.payload.value);
            }

            for (let i = 0; i < state.customizedState.recipe.coated.length; i++) sum = sum + state.customizedState.recipe.coated[i].precentage;

            for (let i = 0; i < state.customizedState.recipe.unCoated.length; i++) sum = sum + state.customizedState.recipe.unCoated[i].precentage;

            state.customizedState.blendPercentages = sum;
        },
        removeItemFromRecipe: (state: AgriSolutionSliceState, action: PayloadAction<{ itemIndex: number; isCoated: boolean }>) => {
            const { itemIndex, isCoated } = action.payload;
            if (isCoated) {
                const item = state.customizedState.recipe.coated.splice(itemIndex, 1);
                if (item[0]) {
                    const newItem: RawMaterial = {
                        id: item[0].id ? item[0].id : 0,
                        name: item[0].name,
                    };
                    state.customizedState.allCoatedMaterials && state.customizedState.allCoatedMaterials.push(newItem);
                }
            } else {
                const item = state.customizedState.recipe.unCoated.splice(itemIndex, 1);
                if (item[0]) {
                    const newItem: RawMaterial = {
                        id: item[0].id ? item[0].id : 0,
                        name: item[0].name,
                    };
                    state.customizedState.allUnCoatedMaterials && state.customizedState.allUnCoatedMaterials.push(newItem);
                }
            }

            let sum = 0;
            for (let i = 0; i < state.customizedState.recipe.coated.length; i++) {
                sum = sum + state.customizedState.recipe.coated[i].precentage;
            }
            for (let i = 0; i < state.customizedState.recipe.unCoated.length; i++) {
                sum = sum + state.customizedState.recipe.unCoated[i].precentage;
            }
            state.customizedState.blendPercentages = sum;
        },
        setFarmerPracticeGraphString: (state: AgriSolutionSliceState, action: PayloadAction<string>) => {
            return {
                ...state,
                releaseGraphState: {
                    ...state.releaseGraphState,
                    farmerPracticeGraphString: action.payload,
                },
            };
        },
        removeItemFromAllCoatedMaterials: (state: AgriSolutionSliceState, action: PayloadAction<{ coated: BlendStat[] }>) => {
            const { coated } = action.payload;
            if (state.customizedState.allCoatedMaterials)
                for (let i = 0; i < coated.length; i++) {
                    const index = state.customizedState.allCoatedMaterials.findIndex((j) => j.id == coated[i].id);
                    state.customizedState.allCoatedMaterials.splice(index, 1);
                }
        },
        removeItemFromAllUncoatedMaterials: (state: AgriSolutionSliceState, action: PayloadAction<{ uncoated: BlendStat[] }>) => {
            const { uncoated } = action.payload;
            if (state.customizedState.allUnCoatedMaterials)
                for (let i = 0; i < uncoated.length; i++) {
                    const index = state.customizedState.allUnCoatedMaterials.findIndex((j) => j.id == uncoated[i].id);
                    state.customizedState.allUnCoatedMaterials.splice(index, 1);
                }
        },
        setBestSolution: (state: AgriSolutionSliceState, action: PayloadAction<string | null>) => {
            state.customizedState.bestSolutionFormula = action.payload;
        },
        setUpdatedBestSolution: (state: AgriSolutionSliceState, action: PayloadAction<string | null>) => {
            state.customizedState.updatedBestSolutionFormula = action.payload;
        },
        setBestSolutionCoatings: (state: AgriSolutionSliceState, action: PayloadAction<string | null>) => {
            state.customizedState.bestSuitFormulaCoatings = action.payload;
        },
        setBestSolutionNutrients: (state: AgriSolutionSliceState, action: PayloadAction<FarmerPractice>) => {
            state.customizedState.formulaNutrients = action.payload;
        },
        setCustomizedFormulaByUser: (state: AgriSolutionSliceState, action: PayloadAction<string>) => {
            state.customizedState.customizedFormulaByUser = action.payload;
        },

        setBlendPrecentage: (state: AgriSolutionSliceState, action: PayloadAction<number | null>) => {
            if (action.payload) state.customizedState.blendPercentages = action.payload;
            else {
                let sum = 0;
                for (let i = 0; i < state.customizedState.recipe.coated.length; i++) sum = sum + state.customizedState.recipe.coated[i].precentage;
                for (let i = 0; i < state.customizedState.recipe.unCoated.length; i++) sum = sum + state.customizedState.recipe.unCoated[i].precentage;
                state.customizedState.blendPercentages = sum;
            }
        },

        setCoatedRawMaterials: (state: AgriSolutionSliceState, action: PayloadAction<RawMaterial[]>) => {
            state.customizedState.allCoatedMaterials = action.payload;
        },

        setUnCoatedRawMaterials: (state: AgriSolutionSliceState, action: PayloadAction<RawMaterial[]>) => {
            state.customizedState.allUnCoatedMaterials = action.payload;
        },

        setUnCoatedRawMaterialsMadeByUser: (state: AgriSolutionSliceState, action: PayloadAction<string[]>) => {
            state.customizedState.unCoatedMaterialsMadeByUser = action.payload;
        },
        setShowMobileMap: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            state.showMobileMap = action.payload;
        },

        setItemId: (state: AgriSolutionSliceState, action: PayloadAction<number | null>) => {
            state.itemId = action.payload;
        },

        setItemName: (state: AgriSolutionSliceState, action: PayloadAction<string>) => {
            state.itemName = action.payload;
        },

        setIsUserSave: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            state.isUserSave = action.payload;
        },

        setSoilStateCanBeNavigated: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            state.soilStateCanBeNavigated = action.payload;
        },

        setResultsPageCanBeNavigated: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            state.resultsStateCanBeNavigated = action.payload;
        },

        setFarmPageCanBeNavigated: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            state.farmStateCanBeNavigated = action.payload;
        },

        setCustomizePageCanBeNavigated: (state: AgriSolutionSliceState, action: PayloadAction<boolean>) => {
            state.customizeStateCanBeNavigated = action.payload;
        },
        setSoil: (state: AgriSolutionSliceState, action: PayloadAction<number>) => {
            console.log("setSoil", action.payload);
            state.temperatureState.selectedSoil = action.payload;
        },
        setWater: (state: AgriSolutionSliceState, action: PayloadAction<number>) => {
            console.log("setWater", action.payload);

            state.temperatureState.selectedWater = action.payload;
        },

        setTemperatureState: (
            state: AgriSolutionSliceState,
            action: PayloadAction<{
                temperatures: { date: number; value: number; index: number }[];
                selectedSoil: number;
                selectedWater: number;
                selectedTemperatureUnit: string | undefined;
            }>
        ) => {
            state.temperatureState = action.payload;
        },
        setMonths: (state: AgriSolutionSliceState, action: PayloadAction<{ date: number; value: number; index: number }[]>) => {
            state.temperatureState.temperatures = action.payload;
        },

        updateTemperatures: (
            state: AgriSolutionSliceState,
            action: PayloadAction<{
                temperatures: { date: number; value: number; index: number }[];
                selectedTemperatureUnit: string | undefined;
            }>
        ) => {
            state.temperatureState = {
                ...state.temperatureState,
                ...action.payload,
            };
        },

        updateTemperatureOfSpecificMonth: (state: AgriSolutionSliceState, action: PayloadAction<{ date: number; value: number; index: number }>) => {
            const newTemperatures = [...state.temperatureState.temperatures];
            newTemperatures.forEach((element) => {
                if (element.date === action.payload.date) {
                    element.value = action.payload.value;
                }
            });
            state.temperatureState = {
                ...state.temperatureState,
                temperatures: newTemperatures,
            };
        },

        setResultsState: (
            state: AgriSolutionSliceState,
            action: PayloadAction<{
                recommendedSolution: CalculateRecommendedSolutionProductResponse | null;
                customizedSolution: CalculateCustomizedSolutionProductResponse | null;
            }>
        ) => {
            state.resultsState = action.payload;
        },

        setStepIndexPage: (state: AgriSolutionSliceState, action: PayloadAction<number>) => {
            state.stepIndexPage = action.payload;
        },
        reset: () => initialState,
        toggleGreenDescription: (state: AgriSolutionSliceState) => {
            return {
                ...state,
                releaseGraphState: {
                    ...state.releaseGraphState,
                    isGreenDescriptionOpen: !state.releaseGraphState.isGreenDescriptionOpen,
                },
            };
        },
    },
});

export const Actions = agriSolution.actions;
export const Reducer = agriSolution.reducer;
